import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '@doccareplus/shared/lib';
import { Button } from '../Button';
import { GlobalLoader } from '../GlobalLoader';
import { useServerReachability } from './useServerReachability';
import { useServerWakeProbe } from './useServerWakeProbe';

const BACKGROUND_REFRESH_THRESHOLD_MS = 3 * 60 * 1000;

/**
 * Cross-cutting overlay driven by the shared server-reachability status, which
 * the api-client sets from real API traffic (all calls, all retries + failover).
 * Rendered once at each app's root (inside its QueryClientProvider) — the shared
 * "global preloader" for every DocCare+ surface.
 *
 * - `online`       → nothing, so normal navigation is never interrupted.
 * - `reconnecting` → the "waking up" loader while a cold-started server spins up
 *                    or the client fails over between servers.
 * - `offline`      → a real, retryable error once every server has been
 *                    exhausted, instead of leaving the misleading loader forever.
 */
export function ServerWakeGate() {
  const { retry } = useServerWakeProbe(); // proactive /health cold-start probe (feeds the store)
  const status = useServerReachability();
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const hiddenAt = useRef<number | null>(null);

  // A quick task switch should not create a burst of repeat requests. If the app
  // was actually in the background long enough for the screen to become stale,
  // refresh its active queries once when the user returns.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt.current = Date.now();
        return;
      }

      const backgroundedAt = hiddenAt.current;
      hiddenAt.current = null;
      if (
        document.visibilityState === 'visible'
        && backgroundedAt !== null
        && Date.now() - backgroundedAt >= BACKGROUND_REFRESH_THRESHOLD_MS
      ) {
        void queryClient.refetchQueries({ type: 'active' });
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [queryClient]);

  // When the server comes back after being unreachable/waking, refresh cached data so
  // permission-gated UI (e.g. the sidebar nav, which hides itself when its access query
  // has no data) repopulates automatically — otherwise it stays collapsed until a
  // re-login. Only a genuine recovery transition triggers this, never a normal online mount.
  const previousStatus = useRef(status);
  useEffect(() => {
    if (previousStatus.current !== 'online' && status === 'online') {
      void queryClient.refetchQueries({ type: 'active' });
    }
    previousStatus.current = status;
  }, [status, queryClient]);

  if (status === 'online') return null;

  if (status === 'offline') {
    const handleRetry = () => {
      // Re-probe /health (the reliable recheck, works even with no active queries)
      // and refresh any cached data once we're back.
      retry();
      void queryClient.refetchQueries({ type: 'active' });
    };

    return (
      <div
        role="alert"
        className="fixed inset-0 z-[100] flex flex-col items-center justify-center gap-4 bg-[var(--bg)] px-6 text-center"
      >
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[var(--danger-bg)] text-[var(--danger)]">
          <svg className="h-7 w-7" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M10.3 3.9L1.8 18a2 2 0 001.7 3h17a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z" />
          </svg>
        </div>
        <h2 className="text-[18px] font-bold text-[var(--text)]">{t('serverWake.errorTitle')}</h2>
        <p className="max-w-[360px] text-[13.5px] leading-relaxed text-[var(--text-2)]">
          {t('serverWake.errorMessage')}
        </p>
        <Button variant="primary" onClick={handleRetry}>
          {t('serverWake.retry')}
        </Button>
      </div>
    );
  }

  return <GlobalLoader message={t('serverWake.message')} />;
}
